import {Pipe, type PipeTransform} from '@angular/core';
import {type EventResponse} from '@middleware';
import {DateFormatterService} from '@services/date/date-formatter.service';

/**
 * A pipe to format event metadata for rendering in different contexts
 */
@Pipe({
  name: 'eventMetadata',
  standalone: true,
})
export class EventMetadataPipe implements PipeTransform {
  constructor(private readonly dateFormatterService: DateFormatterService) {}

  /**
   * Transforms event metadata into a formatted HTML string.
   * @param value The event metadata object.
   * @param isTwoLines A boolean to determine if the output should span two lines.
   * @param location An optional string for the event's location.
   * @returns A formatted string or empty string if the value is null.
   */
  transform(value: EventResponse['event_metadata'] | null, isTwoLines = false, location?: string): string {
    if (!value) {
      return '';
    }

    const startDatetime = value.starts;
    const endDatetime = value.ends;

    const startDate = this.dateFormatterService.formatDate(startDatetime);
    const startTime = this.dateFormatterService.formatTime(startDatetime);
    const startTimeWithTimezone = this.dateFormatterService.formatTimeWithTimezone(startDatetime);
    const endDate = this.dateFormatterService.formatDate(endDatetime);
    const endTime = this.dateFormatterService.formatTimeWithTimezone(endDatetime);

    const isSameDay = startDate === endDate;
    const separator = isTwoLines ? '<br />' : ' ';
    let formattedContent: string;

    // Logic to format the content based on date and time availability
    if (isSameDay) {
      formattedContent =
        `<span class="event-date">${startDate}</span>` +
        `${isTwoLines ? separator : ', '}` +
        `<span class="event-time">${startTime} &ndash; ${endTime}</span>`;
    } else {
      formattedContent =
        `<span class="event-date">${startDate}</span> <span class="event-time">${startTimeWithTimezone}</span> &ndash;` +
        `${separator}` +
        `<span class="event-date">${endDate}</span> <span class="event-time">${endTime}</span>`;
    }

    // Add location to the final output if it exists
    if (location) {
      return `<div class="event-location"><span class="label">Location:</span> ${location}</div><div>${formattedContent}</div>`;
    }

    return formattedContent;
  }
}
